import React from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from '@tanstack/react-router';
import ContentCard from '@/modules/content/common/ContentCard';
import { TContent } from '@/types/Content.types';

// Warm the browser image cache for a card's thumbnail on hover, alongside the router's
// JSON prefetch — images are the heavy part of the detail-page load.
const prefetchImage = (url?: string | null) => {
  if (url) {
    const img = new Image();
    img.src = url;
  }
};

// Cards fade+rise in on mount, staggered by position. Cards are keyed by id, so this plays
// only for newly-appearing cards (first load, a new page appended, a new search/filter
// result) — cards that persist across a re-render don't replay. The stagger is capped so a
// deep page doesn't leave the last cards visibly lagging.
const MAX_STAGGER_S = 0.22;
const entranceDelay = (index: number) => Math.min(index * 0.03, MAX_STAGGER_S);

type Props = {
  content: TContent[];
  // Dimmed + non-interactive while a new filter/search combination loads underneath.
  isDimmed?: boolean;
};

// The card grid shared by every browse surface (ranked feed, search, filtered).
//
// A card always opens the loop, positioned at that item — there is no page/loop mode to
// choose. The detail route is still reached deliberately: "Read Full Article" from the
// loop, and any direct/shared link.
//
// Creator attribution comes from the card payload itself, so there is no per-card
// profile fetch.
const ContentGrid = ({ content, isDimmed = false }: Props) => {
  const reduceMotion = useReducedMotion();

  return (
    <Flex
      width={'100%'}
      gap={'16px'}
      wrap={'wrap'}
      justify={'flex-start'}
      opacity={isDimmed ? 0.5 : 1}
      pointerEvents={isDimmed ? 'none' : 'auto'}
      transition={'opacity 0.2s ease'}
    >
      {content.map((item, index) => (
        <motion.div
          key={item.id}
          initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, delay: entranceDelay(index), ease: 'easeOut' }}
        >
          <Link
            to={'/loop/$contentId'}
            params={{ contentId: item.id }}
            onMouseEnter={() => prefetchImage(item.effectiveThumbnailUrl ?? item.thumbnailUrl)}
            style={{ textDecoration: 'none' }}
          >
            <Box
              role={'group'}
              cursor={'pointer'}
              borderRadius={'14px'}
              // Resting card shadow per Figma (Dawn I panel): 0 0 20 rgba(0,65,121,0.15).
              boxShadow={'0px 0px 20px 0px rgba(0, 65, 121, 0.15)'}
              transition={'transform 0.25s ease, box-shadow 0.25s ease'}
              _hover={{
                transform: 'translateY(-6px)',
                boxShadow: '0px 16px 32px 0px rgba(14, 0, 98, 0.22)',
              }}
            >
              <ContentCard content={item} />
            </Box>
          </Link>
        </motion.div>
      ))}
    </Flex>
  );
};

export default ContentGrid;
